import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { IoIosAdd } from 'react-icons/io'
import { BiTrash } from 'react-icons/bi'
import Button from '../../components/Button'
import Table from '../../components/Table'
import Confirmation from '../../components/Confirmation/Confirmation'
import VaccinationsModal from './Modal'
import styles from './styles.module.scss'
import { getVaccinations, getTypesOfVaccinations, deleteVaccinations } from '../../actions/medCenter/vassinations'

const TABLE_HEADERS = [
  { key: 'vac_type_name', label: 'Тип вакцины' },
  { key: 'name', label: 'Наименование' },
  { key: 'phase', label: 'Компонент' },
  { key: 'actions', label: '', width: '60px' }
]

export default function TypesOfVaccinations() {
  const dispatch = useDispatch()
  const { vaccinations, loading, typesVaccinations } = useSelector(state => state.directoryMedCenter)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortField, setSortField] = useState('vac_type_name')
  const [sortAsc, setSortAsc] = useState(true)

  useEffect(() => {
    dispatch(getTypesOfVaccinations())
    dispatch(getVaccinations())
  }, [dispatch])

  const openModal = useCallback((item) => () => {
    setSelectedItem(item || 'add')
  }, [])

  const closeModal = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const handleDelete = useCallback((item) => (e) => {
    e.stopPropagation()
    setConfirmation({ id: item.c_vaccine, name: item.vac_type_name })
  }, [])

  const acceptConfirmation = useCallback(async () => {
    if (!confirmation) return
    setIsDeleting(true)
    try {
      await dispatch(deleteVaccinations(confirmation.id))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const handleSort = useCallback((field) => {
    if (sortField === field) setSortAsc(v => !v)
    else { setSortField(field); setSortAsc(true) }
  }, [sortField])

  const sortedList = useMemo(() => {
    if (!vaccinations) return []
    return [...vaccinations].sort((a, b) => {
      const aVal = a[sortField] ?? ''
      const bVal = b[sortField] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number')
        return sortAsc ? aVal - bVal : bVal - aVal
      return sortAsc
        ? String(aVal).localeCompare(String(bVal), 'ru')
        : String(bVal).localeCompare(String(aVal), 'ru')
    })
  }, [vaccinations, sortField, sortAsc])

  const isModalOpen = selectedItem !== null
  const isAddMode = selectedItem === 'add'

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <IoIosAdd size={64} className={styles.emptyIcon} />
      <p>Нет добавленных прививок</p>
      <Button onClick={openModal()}>Добавить первую прививку</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка данных...</span>
    </div>
  )

  const renderTable = () => (
    <div
      className={styles.tableContainer}
      style={{ '--table-width': `${TABLE_HEADERS.length * 15}vw` }}
    >
      <Table className={styles.table}>
        <thead>
          <tr>
            {TABLE_HEADERS.map(({ key, label, width }) => (
              <th
                key={key}
                style={{ ...(width ? { width } : {}), ...(key !== 'actions' ? { cursor: 'pointer' } : {}) }}
                onClick={key !== 'actions' ? () => handleSort(key) : undefined}
              >{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedList.map((item) => (
            <tr
              key={item.c_vaccine}
              onClick={openModal(item)}
              className={styles.tableRow}
            >
              <td>
                <span className={styles.itemName}>{item.vac_type_name}</span>
              </td>
              <td>
                <span className={styles.itemDescription}>{item.name}</span>
              </td>
              <td>
                <span>{item.phase}</span>
              </td>
              <td>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleDelete(item)}
                  title={`Удалить ${item.vac_type_name}`}
                  aria-label={`Удалить ${item.vac_type_name}`}
                >
                  <BiTrash size={20} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className={styles.actions}>
        <Button onClick={openModal()}>
          <IoIosAdd size={20} style={{ marginRight: 8 }} />
          Добавить
        </Button>
        <span className={styles.counter}>
          Всего: {sortedList.length}
        </span>
      </div>
    </div>
  )

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1>Виды прививок</h1>
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderTable()}
      </main>

      <VaccinationsModal
        open={isModalOpen}
        onClose={closeModal}
        data={selectedItem}
        addMode={isAddMode}
        typesVaccinations={typesVaccinations}
      />

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить ${confirmation.name}?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}
